// Keeps the member's own feature flags (user-flags) loaded for whoever is signed in.
//
// They decide who gets a targeted feature, a tester's Y21 door for one, and until they land the
// app has only the app flags, which say a row is on but not who it targets. App.vue used to ask
// once per launch, so a failed load (a network blip at sign-in) or a second member signing in on
// the same phone left the app without them until it was restarted.

/** Waits before each retry of a failed load; after the last, only a resume asks again. */
export const MEMBER_FLAGS_RETRY_DELAYS_MS = [5000, 15000, 45000];

/**
 * @param {object} deps
 * @param {(token: string) => Promise<boolean>} deps.loadUserFlags resolves whether they loaded
 * @param {() => boolean} deps.hasMemberFlags
 * @param {() => void} [deps.onSettled] after each attempt, loaded or not
 */
export function createMemberFlagsSync({ loadUserFlags, hasMemberFlags, onSettled = () => {} }) {
  let token = null;
  let retryTimer = null;
  let attempts = 0;
  let inFlight = null;

  const cancelRetry = () => {
    clearTimeout(retryTimer);
    retryTimer = null;
  };

  const load = forToken => {
    cancelRetry();
    const attempt = loadUserFlags(forToken)
      .catch(() => false)
      .then(loaded => {
        // A newer load (another token) may have started meanwhile; that one is still in flight.
        if (inFlight === attempt) inFlight = null;
        onSettled();
        if (loaded || forToken !== token) return;
        const delay = MEMBER_FLAGS_RETRY_DELAYS_MS[attempts];
        attempts += 1;
        if (delay !== undefined) retryTimer = setTimeout(() => load(forToken), delay);
      });
    inFlight = attempt;
    return attempt;
  };

  return {
    /** The signed-in member's token, or null once they sign out. */
    sync(nextToken) {
      if (!nextToken) {
        token = null;
        cancelRetry();
        return Promise.resolve();
      }
      if (nextToken === token) return inFlight || Promise.resolve();
      token = nextToken;
      attempts = 0;
      return load(nextToken);
    },
    /** Back in the foreground: ask again if the member's flags never landed. */
    retryIfMissing() {
      if (!token || inFlight || hasMemberFlags()) return inFlight || Promise.resolve();
      attempts = 0;
      return load(token);
    },
  };
}
